import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  NgZone,
  OnChanges,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { TransferState } from '@angular/platform-browser';
import DevExpress from 'devextreme';
import {
  DxDataGridComponent,
  DxTemplateHost,
  IterableDifferHelper,
  NestedOptionHost,
  WatcherHelper,
} from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import { ExcelExportDataGridProps, exportDataGrid as exportDataGridToExcel } from 'devextreme/excel_exporter';
import { formatMessage } from 'devextreme/localization';
import { exportDataGrid as exportDataGridToPDF } from 'devextreme/pdf_exporter';
import { Column, ContextMenuPreparingEvent, DataType, Scrollable } from 'devextreme/ui/data_grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import { AbcCore, CollectionHelper, CompareHelper, GUID, ObjectHelper } from 'libs/abc/core';
import { ResourceManagerService, ResourceSet } from 'libs/abc/core/resources/src';
import { MessageBoxService } from 'libs/abc/messages/src/lib/messageBox/messageBox.service';
import { EditorService } from 'libs/abc/queries/opath/expressions-editor/src/lib/services/editor.service';
import { AbcCoreContainerService } from '../abcCoreContainer.service';
import {
  AbcGridGroupBy,
  ColumnUpdateArgs,
  CustomQueryColumn,
  CustomQueryContext,
  DataSourceObject,
  DefaultStorage,
  GridAdapter,
  IStorage,
  PrepareDataSourceObjectArgs,
  StorageSettings,
  SummaryItemType,
} from './abc-grid';
import { _AbcGridColumn } from './abc-grid-column';
import { AbcGridContextMenuManager } from './abc-grid-context-menu-manager';

@Component({
  standalone: true,
  selector: 'abc-grid',
  template: '',
  styleUrls: ['./abc-grid.component.scss'],
  providers: [DxTemplateHost, WatcherHelper, IterableDifferHelper, NestedOptionHost, AbcCoreContainerService],
})
export class _AbcGridComponent extends DxDataGridComponent implements OnChanges, AfterViewInit, AfterViewChecked {
  @Input() selectionMode: string;
  @Input() storageSettings: StorageSettings;
  @Output() onColumnUpdate = new EventEmitter<ColumnUpdateArgs>();
  @Output() onPrepareDataSourceObject = new EventEmitter<PrepareDataSourceObjectArgs>();
  @Output() onDataSourceSet = new EventEmitter<void>();
  @Input() setupDefaultLayoutOnInitialization: boolean;
  @Input() autoFilterSelectedRows: boolean;
  private _simpleMode;
  @Input() get simpleMode(): boolean {
    return this._simpleMode;
  }
  set simpleMode(value: boolean) {
    this._simpleMode = value;
    this.updateSimpleMode();
  }
  private customOrDefaultLayoutSetters: Array<() => void> = new Array<() => void>();
  private customOrDefaultLayoutSettersCollected: boolean;
  public contextMenu: AbcGridContextMenuManager;
  private resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  @Input() public markForCheck: boolean = false;

  public get totalCountVisibility(): boolean {
    if (this._totalCountVisibility === undefined) {
      return this.summary?.totalItems?.find((_) => _.summaryType === SummaryItemType.count);
    } else {
      return this._totalCountVisibility;
    }
  }

  private _totalCountVisibility: boolean | undefined = undefined;
  public set totalCountVisibility(visibility: boolean) {
    const visibleColumns: Column[] = this.instance?.getVisibleColumns();
    if (visibleColumns?.length) {
      if (visibility) {
        const columnName = visibleColumns.find((column) => column.name && column.groupIndex === undefined)?.name;
        this.ensureSummaryTotalItem(SummaryItemType.count, columnName);
        this.instance.refresh();
      } else if (this.summary.totalItems) {
        this.ensureSummaryTotalItem(SummaryItemType.count, undefined);
        this.instance.refresh();
      }
      this._totalCountVisibility = undefined;
    } else {
      this._totalCountVisibility = visibility;
      setTimeout(() => {
        this.totalCountVisibility = visibility;
      }, 100);
    }
  }

  public get totalSumColumnNames(): string[] {
    if (this._totalSumColumnNames === undefined) {
      return this.summary?.totalItems?.filter((_) => _.summaryType === SummaryItemType.sum)?.map((_) => _.column);
    } else {
      return this._totalSumColumnNames;
    }
  }

  private _totalSumColumnNames: string[] | undefined = undefined;
  public set totalSumColumnNames(columnNames: string[]) {
    const visibleColumns: Column[] = this.instance?.getVisibleColumns();
    if (visibleColumns?.length) {
      if (columnNames?.length) {
        for (const columnName of columnNames) {
          const column = visibleColumns.find((column) => column.name === columnName && column.groupIndex === undefined);
          if (this.isTotalSumAllowed(column)) {
            this.ensureSummaryTotalItem(SummaryItemType.sum, columnName);
          } else {
            this.removeSummaryTotalItem(SummaryItemType.sum, columnName);
          }
        }
        this.instance.refresh();
      } else if (this.summary.totalItems) {
        this.removeSummaryTotalItem(SummaryItemType.sum, undefined);
        this.instance.refresh();
      }
      this._totalSumColumnNames = undefined;
    } else {
      if (!columnNames?.length && this.summary.totalItems) {
        this.removeSummaryTotalItem(SummaryItemType.sum, undefined);
      }
      this._totalSumColumnNames = columnNames;
      setTimeout(() => {
        this.totalSumColumnNames = columnNames;
      }, 100);
    }
  }

  public set columns(columns: _AbcGridColumn[]) {
    if (this.storageSettings?.key) {
      if (this.instance?.state) {
        this.instance.state(null);
      }
    }
    super.columns = columns;
    if (this.storageSettings?.key) {
      this.restoreLayout();
    }
  }

  public get columns(): _AbcGridColumn[] {
    return super.columns as _AbcGridColumn[];
  }

  public get groupPanelVisibility(): boolean | string {
    return this.groupPanel.visible;
  }

  public set groupPanelVisibility(visibility: boolean | string) {
    this.groupPanel.visible = visibility;
    this.ensureGroupByPanelVisibility(visibility === true);
    setTimeout(() => {
      this.instance.repaint();
    }, 10);
  }

  public getCustomContext: () => Promise<CustomQueryContext>;
  public loadCustomColumn: (customColumn: CustomQueryColumn) => Promise<void>;
  public refreshCustomColumn: (customColumn: CustomQueryColumn) => Promise<void>;
  public deleteCustomColumn: (customColumnName: string) => Promise<void>;
  public clearCustomColumns: () => Promise<void>;
  public layoutReset: () => Promise<void>;
  public objectsSetsInstance: () => Promise<void>;

  constructor(
    elementRef: ElementRef,
    ngZone: NgZone,
    templateHost: DxTemplateHost,
    _watcherHelp: WatcherHelper,
    _idh: IterableDifferHelper,
    optionHost: NestedOptionHost,
    transferState: TransferState,
    @Inject(PLATFORM_ID) platformId: any,
    private resourceService: ResourceManagerService,
    private messageBox: MessageBoxService,
    public abcCore: AbcCore,
    private editorService: EditorService
  ) {
    super(elementRef, ngZone, templateHost, _watcherHelp, _idh, optionHost, transferState, platformId);
    this.storageSettings = new StorageSettings(this);
    this.resAsync = this.resourceService.getResourceSet('abc-ui-components-abcGrid');

    this.clearCustomColumns = async () => {
      if (this.columns?.length) {
        for (const c of this.columns.map((_) => _)) {
          if (c[AbcGridContextMenuManager.CustomOPathColumnKey]) {
            if (this.deleteCustomColumn) {
              await this.deleteCustomColumn(c.name);
            }
            this.deleteColumn(c.name);
          }
        }
      }
    };

    let isUpdating = false;
    let updateTotalCountVisibility = false;
    let saveLayout = false;
    this.onOptionChanged.subscribe((e) => {
      if (this.isRestoringState || this.isResettingLayout) {
        return;
      }
      if ((e.name === 'columns' && e.fullName !== 'columns') || e.fullName === 'filterPanel.filterEnabled') {
        //auto enable filterPanel if filter is in column applied
        if ((e.fullName.endsWith('filterValue') || e.fullName.endsWith('filterValues')) && e.value) {
          this.filterPanel.visible = true;
        }
        if (e.fullName.endsWith('visible')) {
          let columnindex = e.fullName.substring(e.fullName.lastIndexOf('[') + 1, e.fullName.lastIndexOf(']'));
          const columnUpdateArgs: ColumnUpdateArgs = new ColumnUpdateArgs();
          columnUpdateArgs.column = this.columns[columnindex];
          columnUpdateArgs.visibility = e.value;
          this.onColumnUpdate.emit(columnUpdateArgs);
        }
        if (e.fullName?.endsWith('.visibleIndex') || e.fullName?.endsWith('.fixed')) {
          updateTotalCountVisibility = true;
        }
        this.syncSettings();
        if (this.stateStoring?.enabled) {
          saveLayout = true;
        }
        if (!isUpdating) {
          isUpdating = true;
          setTimeout(() => {
            try {
              if (updateTotalCountVisibility) {
                this.ensureTotalCountVisibility();
                updateTotalCountVisibility = false;
              }
              if (saveLayout) {
                saveLayout = false;
                this.saveLayout();
              }
            } finally {
              isUpdating = false;
            }
          }, 100);
        }
      }
    });
  }

  private async initContextMenu(): Promise<void> {
    this.contextMenu = new AbcGridContextMenuManager(
      this,
      this.abcCore,
      this.messageBox,
      await this.resAsync,
      this.editorService
    );
    this.onContextMenuPreparing.subscribe(async (event: ContextMenuPreparingEvent) => {
      this.contextMenu.prepareGridDataContext(<_AbcGridColumn>event.column);
      if (event.column || event.items?.length || !this.showColumnHeaders) {
        event.items = (event.items ?? []).concat(this.contextMenu.defaultGridContextMenu());
      }
    });
  }

  private ensureGroupByPanelVisibility(visible: boolean) {
    setTimeout(() => {
      const groupDiv = document.querySelector('.dx-datagrid-header-panel') as HTMLElement;
      if (groupDiv) {
        groupDiv.style.display = visible ? AbcGridGroupBy.Block : AbcGridGroupBy.None;
      }
    }, 100);
  }

  public getRowData(rowIndex) {
    let row;
    let rows = this.instance.getVisibleRows();
    rows.forEach((element) => {
      if (element.rowIndex === rowIndex) {
        row = element.data;
      }
    });
    return row;
  }

  private ensureCustomOrDefaultPropertyValue<T>(
    propertySetter: (propValue: T) => void,
    customValueGetter: () => T,
    defaultValue: T
  ) {
    let customValue = customValueGetter ? customValueGetter() : undefined;
    if (customValue) {
      if (ObjectHelper.isObject(customValue) && ObjectHelper.isObject(defaultValue)) {
        customValue = ObjectHelper.mergeDeep(defaultValue, customValue);
      }
      this.customOrDefaultLayoutSetters.push(() => {
        propertySetter(customValue);
      });
    } else {
      this.customOrDefaultLayoutSetters.push(() => propertySetter(defaultValue));
    }
  }

  private collectCustomAndDefaultLayout(): void {
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.showColumnHeaders = propValue),
      () => this.showColumnHeaders,
      true
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.allowColumnReordering = propValue),
      () => this.allowColumnReordering,
      true
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.allowColumnResizing = propValue),
      () => this.allowColumnResizing,
      true
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.columnChooser = propValue),
      () => this.columnChooser,
      {
        enabled: false,
        sortOrder: 'asc',
        title: 'Spaltenauswahl',
        search: { enabled: true },
      }
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.pager = propValue),
      () => this.pager,
      {
        visible: false,
      }
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.filterRow = propValue),
      () => this.filterRow,
      {
        visible: true,
      }
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.filterPanel = propValue),
      () => this.filterPanel,
      {
        visible: false,
      }
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.filterSyncEnabled = propValue),
      () => this.filterSyncEnabled,
      true
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.headerFilter = propValue),
      () => this.headerFilter,
      {
        visible: true,
        search: { enabled: true },
      }
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.loadPanel = propValue),
      () => this.loadPanel,
      {
        enabled: false,
      }
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.showBorders = propValue),
      () => this.showBorders,
      false
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.showRowLines = propValue),
      () => this.showRowLines,
      true
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.showColumnLines = propValue),
      () => this.showColumnLines,
      true
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.width = propValue),
      () => this.width,
      '100%'
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.height = propValue),
      () => this.height,
      '100%'
    );
    this.ensureCustomOrDefaultPropertyValue(
      (propValue) => (this.noDataText = propValue),
      () => this.noDataText,
      ''
    );
  }

  private setupCustomOrDefaultLayout(): void {
    for (let i = 0; i < this.customOrDefaultLayoutSetters.length; i++) {
      this.customOrDefaultLayoutSetters[i]();
    }
  }

  private setupBasicLayout(): void {
    this.noDataText = '';
  }

  ngOnChanges(): void {
    if (!this.customOrDefaultLayoutSettersCollected) {
      this.customOrDefaultLayoutSettersCollected = true;
      this.collectCustomAndDefaultLayout();
    }
  }

  ngAfterViewChecked(): void {
    this.updateSimpleMode();
  }

  async ngAfterViewInit(): Promise<void> {
    this.res = await this.resAsync;
    await this.initContextMenu();
    this.setupBasicLayout();
    if (this.setupDefaultLayoutOnInitialization) {
      this.setupCustomOrDefaultLayout();
    }
    this.stateStoring.type = 'custom';
    this.stateStoring.savingTimeout = 1000;
    this.syncSettings();

    if (this.selectionMode) {
      this.selection.mode = this.selectionMode;
    }

    if (!this.paging?.pageSize) {
      this.updatePaging(this.dataSource as any);
    }
    this.instance.endUpdate();

    if (this.markForCheck) {
      this.cdr.detectChanges();
    }

    super.ngAfterViewInit();
  }

  private isRestoringState = false;
  public async restoreLayout(): Promise<void> {
    //this.instance.beginUpdate();
    if (this.instance?.state && this.storageSettings.key) {
      const rawstate = await this.getState(this.storageSettings.key);
      if (rawstate || !this.setupDefaultLayoutOnInitialization) {
        this.isRestoringState = true;
        try {
          const state = await this.getAndEnsureStoredLayoutState();
          this.instance.state(state);
        } finally {
          this.isRestoringState = false;
        }
      } else {
        this.instance.state(null);
      }
    } else if (this.stateStoring && this.storageSettings.key) {
      const rawstate = await this.getState(this.storageSettings.key);
      if (rawstate || !this.setupDefaultLayoutOnInitialization) {
        this.isRestoringState = true;
        this.stateStoring.customLoad = async () => {
          const state = await this.getAndEnsureStoredLayoutState();
          this.stateStoring.customLoad = null;
          setTimeout(() => {
            this.isRestoringState = false;
          }, 100);
          return state;
        };
      }
    }

    this.ensureTotalCountVisibility();
    // this.instance.endUpdate();
  }

  private ensureTotalCountVisibility(): void {
    const tItem = this.summary?.totalItems?.find((_) => _.summaryType === SummaryItemType.count);
    if (tItem) {
      const firstVisibleColumn = this.instance?.getVisibleColumns()?.find((_) => true);
      if (firstVisibleColumn && tItem.column !== firstVisibleColumn.name) {
        tItem.column = firstVisibleColumn.name;
        this.summary = this.summary;
      }
    }
  }

  private isResettingLayout = false;
  public async saveLayout(): Promise<void> {
    if (!this.isResettingLayout && this.instance?.state && this.storageSettings.key) {
      const state = this.instance.state();
      this.storeLayoutState(state);
    }
  }

  public async resetLayout(): Promise<void> {
    if (this.isResettingLayout) {
      return;
    }
    this.isResettingLayout = true;
    try {
      if (this.filterValue) {
        try {
          this.filterValue = undefined;
        } catch {}
      }
      if (this.clearCustomColumns) {
        await this.clearCustomColumns();
      }
      this.showColumnHeaders = true;
      this.totalCountVisibility = false;
      this.totalSumColumnNames = [];

      if (this.instance?.state) {
        this.instance.state(null);
      }
      const key = this.storageSettings.key;
      if (key && this.stateStoring.storageKey === key) {
        this.enableStateStoring(false);
        await this.storeLayoutStateCore(key, null);
        this.enableStateStoring(true);
      }

      if (this.setupDefaultLayoutOnInitialization) {
        this.setupCustomOrDefaultLayout();
      }

      if (this.layoutReset) {
        await this.layoutReset();
      }
    } finally {
      this.isResettingLayout = false;
    }
  }

  private lastSavedState: any;
  private lastSavedKey: string;

  private async storeLayoutState(state: any): Promise<void> {
    const key = this.storageSettings.key;
    if (key && this.stateStoring.storageKey === key) {
      if (!state) {
        state = {};
      }
      state.columns?.forEach((col, index) => {
        if (col) {
          const orig = this.columns?.find((_) => _.dataField === col.dataField);
          if (orig) {
            const caption = orig.caption;
            if (caption) {
              col.caption = caption;
            }
            const customOPath = orig[AbcGridContextMenuManager.CustomOPathColumnKey];
            if (customOPath) {
              col[AbcGridContextMenuManager.CustomOPathColumnKey] = customOPath;
            }
          }
        }
      });

      //ensure existing properties
      if (!state.filterPanel) {
        state.filterPanel = {};
      }
      if (!state.headerFilter) {
        state.headerFilter = {};
      }
      if (!state.filterRow) {
        state.filterRow = {};
      }
      if (!state.groupPanel) {
        state.groupPanel = {};
      }

      if (!this.storageSettings.includeFiltering) {
        state.filterPanel.visible = false;
        state.headerFilter.visible = false;
        state.filterRow.visible = false;
        state.filterValue = null;
        for (var i = 0; i < state.columns?.length; i++) {
          state.columns[i].filterType = null;
          state.columns[i].filterValue = null;
          state.columns[i].filterValues = null;
        }
      } else {
        state.filterPanel.visible = this.filterPanel.visible;
        state.headerFilter.visible = this.headerFilter.visible;
        state.filterRow.visible = this.filterRow.visible;

        state.filterValue = this.cleanupFilterValue(state.filterValue);
        for (var i = 0; i < state.columns?.length; i++) {
          state.columns[i].filterValue = this.cleanupFilterValue(state.columns[i].filterValue);
          state.columns[i].filterValues = this.cleanupFilterValue(state.columns[i].filterValues);
        }
      }

      state.groupPanel.visible = this.groupPanelVisibility;
      state.totalCountVisibility = this.totalCountVisibility;
      state.totalSumColumnNames = this.totalSumColumnNames;
      state.showColumnHeaders = this.showColumnHeaders;

      this.prepareSavedState(state);

      await this.storeLayoutStateCore(key, state);
    }
  }

  private cleanupFilterValue(filterValue: any): any {
    if (filterValue) {
      if (Array.isArray(filterValue)) {
        const newValues = [];
        for (let i = 0; i < filterValue.length; i++) {
          const newValue = this.cleanupFilterValue(filterValue[i]);
          if (newValue || !filterValue[i]) {
            newValues.push(newValue);
          } else if (
            newValues.length > 0 &&
            typeof newValues[newValues.length - 1] === 'string' &&
            (newValues[newValues.length - 1] === 'and' ||
              newValues[newValues.length - 1] === 'or' ||
              newValues[newValues.length - 1] === 'anyof')
          ) {
            if (newValues.length === 2 && newValues[newValues.length - 1] === 'anyof') {
              newValues.splice(newValues.length - 2, 2);
            } else {
              newValues.splice(newValues.length - 1, 1);
            }
          }
        }
        if (newValues.length) {
          if (
            newValues.length > 1 &&
            Array.isArray(newValues[1]) &&
            (newValues[0] === 'and' || newValues[0] === 'or')
          ) {
            newValues.splice(0, 1);
          }
          filterValue = newValues;
        } else {
          filterValue = undefined;
        }
      } else if (typeof filterValue === 'string' && GUID.isGUID(filterValue)) {
        filterValue = undefined;
      }
    }
    return filterValue;
  }

  private async storeLayoutStateCore(key: string, state: any): Promise<void> {
    if (
      !this.lastSavedKey ||
      !this.lastSavedState ||
      this.lastSavedKey !== key ||
      !CompareHelper.areDeepEqual(state, this.lastSavedState)
    ) {
      this.setLastSavedState(key, state);
      await this.setState(key, state);
    }
  }

  private isFirstLayoutLoad: boolean = true;
  private async getAndEnsureStoredLayoutState(customColumns?: CustomQueryColumn[]): Promise<any> {
    let state = await this.getStoredLayoutState(!!customColumns);
    if (customColumns) {
      if (state?.columns) {
        for (const col of state?.columns) {
          if (col?.dataField) {
            const customOPath: string = col[AbcGridContextMenuManager.CustomOPathColumnKey];
            if (customOPath) {
              customColumns.push({
                name: col.dataField,
                opath: customOPath,
                caption: col.caption,
                visibleIndex: col.visibleIndex,
              });
            }
          }
        }
      }
      return state;
    }
    if (state) {
      this.summary = {};
      if (this.isFirstLayoutLoad || !this.storageSettings.keepGlobalLayoutOptions) {
        if (this.storageSettings.includeFiltering) {
          this.filterPanel.visible = state.filterPanel?.visible;
          this.headerFilter.visible = state.headerFilter?.visible;
          this.filterRow.visible = state.filterRow?.visible;
        }

        this.updatePaging();

        this.groupPanelVisibility = state.groupPanel?.visible;
        this.totalCountVisibility = state.totalCountVisibility;
        if (typeof state.showColumnHeaders === 'boolean') {
          this.showColumnHeaders = state.showColumnHeaders;
        } else {
          this.showColumnHeaders = true;
        }
      }
      this.isFirstLayoutLoad = false;

      this.totalSumColumnNames = state.totalSumColumnNames;

      if (state?.columns) {
        for (const col of state?.columns) {
          if (col?.dataField) {
            const orig = this.columns?.find((_) => _.dataField === col.dataField);
            if (orig && orig.visible != col.visible) {
              orig.visible = col.visible;
            }
          }
        }
      }
    }
    return state;
  }

  public async getStoredLayoutState(ignoreKey?: boolean): Promise<any> {
    const key = this.storageSettings.key;
    if (key && (ignoreKey || this.stateStoring?.storageKey === key)) {
      let state = await this.getState(key);
      if (!state) {
        state = {};
        const columns: { dataField: string; visible: boolean; visibleIndex: number }[] = [];
        state.columns = columns;
        if (this.columns?.length) {
          let index = this.columns.length;
          for (const c of this.columns) {
            const column = {
              dataField: c.dataField,
              visible: c.visible ?? true,
              visibleIndex: c.visibleIndex ?? index++,
            };
            column[AbcGridContextMenuManager.CustomOPathColumnKey] = c[AbcGridContextMenuManager.CustomOPathColumnKey];
            GridAdapter.prepareGridColumn(c);
            columns.push(column);
          }
        }
      }

      if (state) {
        if (!this.storageSettings.includeFiltering) {
          for (var i = 0; i < state.columns?.length; i++) {
            state.columns[i].filterValue = null;
          }
          state.filterValue = null;
          if (!state.filterPanel) {
            state.filterPanel = {};
          }
          state.filterPanel.visible = false;
          state.filterPanel.filterEnabled = false;
        }

        if (!this.storageSettings.includeSorting) {
          for (var i = 0; i < state.columns?.length; i++) {
            state.columns[i].sortIndex = null;
            state.columns[i].sortOrder = null;
          }
        }

        if (state.columns && this.columns) {
          const validColumns: any[] = [];
          const currentColumns: Map<string, DevExpress.ui.dxDataGrid.Column<any, any>> = new Map();
          if (this.columns) {
            for (const c of this.columns) {
              currentColumns.set(c.dataField, c);
            }
          }
          for (var i = 0; i < state.columns.length; i++) {
            const colStored = state.columns[i];
            if (colStored?.dataField) {
              const colCurrent = currentColumns.get(colStored.dataField);
              if (colCurrent || colStored[AbcGridContextMenuManager.CustomOPathColumnKey]) {
                currentColumns.delete(colStored.dataField);
                validColumns.push(colStored);
              }
            }
          }
          for (const e of Array.from(currentColumns)) {
            const obj = {};
            Object.assign(obj, e[1]);
            validColumns.push(obj);
          }
          state.columns = validColumns;
          if (this.storageSettings.includeFiltering) {
            if (this.isInvalidFilterValue(state.filterValue, validColumns)) {
              state.filterValue = null;
              for (var i = 0; i < state.columns?.length; i++) {
                state.columns[i].filterValue = null;
              }
            }
          }
        }

        this.prepareSavedState(state);
        this.setLastSavedState(key, state);
      }
      return state;
    }
  }

  private isInvalidFilterValue(filterValue: any, validColumns: any[]) {
    if (Array.isArray(filterValue)) {
      if (
        filterValue.length === 3 &&
        typeof filterValue[0] === 'string' &&
        !validColumns?.find((_) => _.dataField === filterValue[0])
      ) {
        return true;
      }
      for (const e in filterValue) {
        if (this.isInvalidFilterValue(e, validColumns)) {
          return true;
        }
      }
    }
    return false;
  }

  public get focusedDataRowKey(): any {
    const key = this.focusedRowKey;
    if (key && this.isNonDataRow(key)) {
      return null;
    }
    return key;
  }

  private isNonDataRow(key: any): boolean {
    return !!this.instance
      ?.getVisibleRows()
      .find(
        (_) =>
          _.key == key &&
          (_.rowType?.startsWith('group') ||
            _.rowType?.startsWith('filter') ||
            _.rowType?.startsWith('header') ||
            _.rowType?.startsWith('total'))
      );
  }

  public async getFocusedRowData(): Promise<any> {
    const key = this.focusedDataRowKey;
    if (key) {
      return await this.instance?.byKey(key);
    }
  }

  private prepareSavedState(state: any): void {
    delete state.focusedRowKey;
    delete state.selectedRowKeys;
    if (!this.storageSettings.includePaging) {
      delete state.pageSize;
      delete state.pageIndex;
      delete state.allowedPageSizes;
    }
    const arr: any[] = state.columns;
    if (arr) {
      let i = 0;
      while (i < arr.length) {
        const column = arr[i];
        if (column.dataField) {
          i++;
        } else {
          arr.splice(i, 1);
        }
      }
    }
    ObjectHelper.cleanupUndefinedValues(state);
  }

  private setLastSavedState(key: string, state: any): void {
    this.lastSavedKey = key;
    this.lastSavedState = ObjectHelper.cloneDeep(state);
  }

  //whats included in state storage:
  //https://js.devexpress.com/Documentation/ApiReference/UI_Components/dxDataGrid/Configuration/stateStoring/
  private enableStateStoring(enabled: boolean): void {
    if (this.stateStoring) {
      if (enabled && this.stateStoring.enabled) {
        this.stateStoring.customSave = async (state) => await this.storeLayoutState(state);
      } else {
        this.stateStoring.enabled = false;
        this.stateStoring.customSave = null;
      }
    } else if (enabled) {
      setTimeout(() => {
        this.enableStateStoring(true);
      }, 100);
    }
  }

  public setGridOptions(option: string, optionValue: any) {
    if (option === 'groupable') {
      this.groupPanelVisibility = optionValue;
    } else if (option === 'columnFilter') {
      this.headerFilter.visible = optionValue;
      if (optionValue === false) {
        this.instance.clearFilter();
      }
    } else if (option === 'filterPanel') {
      this.filterPanel.visible = optionValue;
      // this.instance.clearFilter();
    } else if (option === 'showfilterrow') {
      this.filterRow.visible = optionValue;
      // this.instance.clearFilter();
    } else if (option === 'columnResize') {
      this.allowColumnResizing = optionValue;
    } else if (option === 'selectionMode') {
      this.selection.mode = optionValue;
      this.instance.repaint();
    } else {
      this.instance.option(option, optionValue);
    }
  }

  public copyColumnDataToClipBoard(columnDataField: string): Array<any> {
    const ds = this.instance.getDataSource();
    const usesPages = ds.paginate();
    try {
      if (usesPages) {
        ds.paginate(false);
        ds.load();
      }
      const data = ds.items();
      const columnData = data
        .map((item) => item[columnDataField])
        ?.filter((value, index, self) => {
          if (value && self.indexOf(value) === index) {
            return true;
          } else {
            return false;
          }
        });
      return columnData;
    } finally {
      if (usesPages) {
        ds.paginate(true);
        ds.load();
      }
    }
  }

  public pasteDataFromClipBoard(columnHeaderName: string, data: string | Array<any>): void {
    this.filterPanel.visible = true;
    let filterValues = this.filterValue || [];
    if (typeof data !== 'string') {
      if (this.filterValue && typeof this.filterValue[0] === 'string') {
        this.filterValue[0] = [this.filterValue[0], this.filterValue[1], this.filterValue[2]];
        delete this.filterValue[1];
        delete this.filterValue[2];
      }
      const filterExpr = data.map((someText) => {
        return someText.replace(/(\r\n|\n|\r)/gm, '');
      });
      filterValues.push('and');
      filterValues.push([columnHeaderName, 'anyof', filterExpr]);
    } else {
      filterValues.push([columnHeaderName, this.getFilterOperation('string'), data]);
    }
    this.setCustomFilterText(filterValues, true);
    this.filterValue = filterValues;
  }

  private getFilterOperation(type: string): string {
    switch (type) {
      case 'string':
        return '=';
      case 'number':
        return '=';
      case 'date':
        return '=';
      default:
        return '=';
    }
  }

  //BEC: ifDateTimeMakeDateTimeFormat = the ugliest sh*t ever, but nobody know why someone decided this:
  //dataType === 'datetime' =  return 'HH:mm'
  public static getColumnFormat(
    dataType: DataType,
    editMaskOrFormat?: string | unknown,
    ifDateTimeMakeDateTimeFormat?: boolean
  ): DevExpress.ui.Format {
    if (typeof editMaskOrFormat === 'string' && editMaskOrFormat !== 'TIMESPAN') {
      return this.editMaskReplaceSpecialCharacter(editMaskOrFormat);
    }
    if (dataType === 'date') {
      return <any>{ year: 'numeric', month: '2-digit', day: '2-digit' };
    }
    if (dataType === 'datetime') {
      if (ifDateTimeMakeDateTimeFormat) {
        return <any>{ year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' };
      } else {
        return 'HH:mm';
      }
    }
    if (editMaskOrFormat) {
      return this.getFormatFromEditMask(editMaskOrFormat);
    }
  }

  public static getFormatFromEditMask(editMaskOrFormat: string | unknown): DevExpress.ui.Format {
    if (editMaskOrFormat === 'TIMESPAN') {
      const unitsAndFactors = [
        { unit: 'd', factor: 1.0 },
        { unit: 'h', factor: 24.0 },
        { unit: 'min', factor: 24.0 * 60.0 },
      ];
      let lasttextvalue: string = undefined;
      let lasttextdate = Date.now();
      return {
        parser: (value: string) => {
          let factor = unitsAndFactors[0].factor;
          let v = value;
          for (const e of unitsAndFactors) {
            if (v?.endsWith(' ' + e.unit)) {
              factor = e.factor;
              v = v.split(' ')[0];
              break;
            }
          }
          if (lasttextvalue !== value) {
            lasttextvalue = value;
            lasttextdate = Date.now();
          }
          return Number.parseFloat(v.split(' ')[0].replace(',', '.')) / factor;
        },
        formatter: (value: number) => {
          let textv = lasttextvalue;
          const d = lasttextdate;
          if ((textv && d && Date.now() - d < 100) || (!CompareHelper.isNullorEmpty(value) && isNaN(value))) {
            return textv;
          }
          lasttextdate = undefined;
          let unit = unitsAndFactors[unitsAndFactors.length - 1].unit;
          let factor = unitsAndFactors[unitsAndFactors.length - 1].factor;
          const rounding = 10000;
          if (value) {
            for (const e of unitsAndFactors) {
              const v = value * e.factor;
              if (Math.abs(v - Math.floor(v)) < 1.0 / rounding) {
                factor = e.factor;
                unit = e.unit;
                break;
              }
            }
          }
          return (
            (Math.round(value * factor * rounding) / rounding)?.toLocaleString(document.documentElement.lang, {
              useGrouping: false,
            }) +
            ' ' +
            unit
          );
        },
      };
    }
    return editMaskOrFormat;
  }

  private static editMaskReplaceSpecialCharacter(editMask: string): string {
    if (editMask) {
      if (editMask.indexOf('a') > -1) return editMask.replace(/\\a/g, 'a');
      if (editMask.indexOf('M') > -1) return editMask.replace(/\\M/g, 'M');
      if (editMask.indexOf('d') > -1) return editMask.replace(/\\d/g, 'd');
      if (editMask.indexOf('Q') > -1) return editMask.replace(/\\Q/g, 'Q');
      if (editMask.indexOf('Y') > -1) return editMask.replace(/\\Y/g, 'Y');
      if (editMask.indexOf('E') > -1) return editMask.replace(/\\E/g, 'E');
      if (editMask.indexOf('h') > -1) return editMask.replace(/\\h/g, 'h');
      if (editMask.indexOf('H') > -1) return editMask.replace(/\\H/g, 'H');
      if (editMask.indexOf('m') > -1) return editMask.replace(/\\m/g, 'm');
      if (editMask.indexOf('s') > -1) return editMask.replace(/\\s/g, 's');
      if (editMask.indexOf('S') > -1) return editMask.replace(/\\S/g, 'S');
      return editMask;
    }
  }

  public async getFilteredObjects(): Promise<any[]> {
    const filterExpr = this.instance.getCombinedFilter(true);
    const filteredObjects = (await this.instance.getDataSource().store().load({ filter: filterExpr })) as any[];
    return filteredObjects;
  }

  @Input()
  public get dataSourceObject(): DataSourceObject {
    return {
      dataSource: <any[] | DataSource<any, any>>this.dataSource,
      gridColumns: this.columns,
      storageKey: this.storageSettings.key,
      storage: this.storageSettings.storage,
    };
  }

  public set dataSourceObject(dataSourceObject: DataSourceObject) {
    if (dataSourceObject?.storage) {
      this.storageSettings.storage = dataSourceObject?.storage;
    }
    if (dataSourceObject?.storageKey) {
      this.storageSettings.key = dataSourceObject?.storageKey;
    }
    const args: PrepareDataSourceObjectArgs = { grid: this, dataSourceObject: dataSourceObject };
    this.onPrepareDataSourceObject.emit(args);
    setTimeout(async () => {
      let dataSource = args.dataSourceObject?.dataSource;
      if (args.getDataSource) {
        const ds = await args.getDataSource();
        if (ds) {
          dataSource = ds;
        }
      }
      this.setDataSource(
        dataSource,
        () => Promise.resolve(dataSourceObject?.gridColumns),
        dataSourceObject?.storageKey,
        dataSourceObject?.focusedRowKey,
        dataSourceObject?.getSelectedRowKeys ? dataSourceObject?.getSelectedRowKeys(dataSource) : undefined
      );
    }, 100);
  }

  public async setDataSource(
    dataSource: any[] | DataSource<any, any>,
    getColumns: () => Promise<_AbcGridColumn[]>,
    storageKey: string,
    focusedRowKey?: any,
    selectedRowKeys?: any[]
  ): Promise<void> {
    this.enableStateStoring(false);
    this.storageSettings.key = null;
    this.totalSumColumnNames = [];

    if (this.instance?.getScrollable) {
      let scroll = this.instance?.getScrollable();
      scroll?.scrollTop();
      scroll?.scrollLeft();
    }
    if (Array.isArray(dataSource)) {
      this.dataSource = [];
    } else {
      this.dataSource = undefined;
    }
    if (this.instance?.state) {
      this.instance.state(null);
    }
    super.columns = [];

    this.storageSettings.key = storageKey;
    if (getColumns) {
      super.columns = await getColumns();
    }
    this.updatePaging(dataSource);
    this.dataSource = dataSource;

    try {
      await this.restoreLayout();
    } catch (err) {
      console.log(err);
    }

    if (focusedRowKey) {
      this.ensureRowVisible(focusedRowKey);
      this.focusedRowKey = focusedRowKey;
    }

    this.setSelectedRowKeys(selectedRowKeys);

    this.syncSettings();

    if (!focusedRowKey && this.instance?.getScrollable) {
      let scroll = this.instance?.getScrollable();
      scroll?.scrollTop();
      scroll?.scrollLeft();
    }
    this.onDataSourceSet.emit();
  }

  public setSelectedRowKeys(selectedRowKeys: Array<any>): void {
    if (selectedRowKeys?.length) {
      this.selectedRowKeys = [];
      this.selectedRowKeys = selectedRowKeys;
      if (this.autoFilterSelectedRows) {
        this.filterByRowKeys(selectedRowKeys);
      }
    }
  }

  public filterByRowKeys(rowKeys: Array<any>): void {
    if (rowKeys?.length) {
      const filter: any[] = [];
      filter.push([this.keyExpr, 'anyof', rowKeys]);

      this.filterPanel.visible = true;
      this.setCustomFilterText(filter, false);
      this.filterValue = filter;
    }
  }

  private setCustomFilterText(filter: any[], isSelectedFromClipboard: boolean) {
    let filterText: string = isSelectedFromClipboard ? 'clipboard selection' : 'initial selection';
    let filterTextCustom: string = 'custom selection';
    this.filterPanel.customizeText = (e) => {
      if (e.filterValue === filter) {
        return filterText;
      } else {
        return filterTextCustom;
      }
    };
    this.resAsync.then((_) => {
      if (isSelectedFromClipboard) {
        filterText = _.get('FilterCaption-ClipboardSelection');
      } else {
        filterText = _.get('FilterCaption-InitialSelection');
      }
      filterTextCustom = _.get('FilterCaption-Custom');
    });
  }

  public async ensureRowVisible(rowKey: string, inFirstVisibleRowsCount?: number): Promise<void> {
    if (CompareHelper.isNullorEmpty(rowKey)) {
      return;
    }

    if (!inFirstVisibleRowsCount) {
      inFirstVisibleRowsCount = 5;
    }
    let found = false;
    if (inFirstVisibleRowsCount > 0 && this.focusedRowEnabled && this.keyExpr) {
      const visibleRows = this.instance?.getVisibleRows().slice(0, inFirstVisibleRowsCount);
      if (visibleRows) {
        for (const r of visibleRows) {
          if (r.key == rowKey) {
            found = true;
            break;
          }
        }
      }
    }
    if (!found) {
      await this.instance?.navigateToRow(rowKey);
    }
  }

  public setColumnVisibleIndex(column: _AbcGridColumn, visibleIndex: number) {
    if (CompareHelper.isNullorEmpty(visibleIndex)) {
      this.instance?.columnOption(column.name, 'visibleIndex', this.instance?.getVisibleColumns()?.length ?? 0);
    } else if (visibleIndex >= 0) {
      const arr = this.instance
        .getVisibleColumns()
        .filter((c) => c?.name !== column?.name)
        .map((_) => _ as _AbcGridColumn);
      if (visibleIndex < arr.length) {
        CollectionHelper.insertAt(arr, visibleIndex, column);
      } else {
        arr.push(column);
      }
      let index = 0;
      for (const c of arr.reverse()) {
        c.visibleIndex = arr.length - 1 - index++;
        this.instance?.columnOption(c.name, 'visibleIndex', c.visibleIndex);
      }
    } else {
      column.visibleIndex = -1;
      this.instance?.columnOption(column.name, 'visibleIndex', column.visibleIndex);
    }
  }

  private updatePaging(dataSource?: any[] | DataSource<any, any>) {
    if (!this.storageSettings.includePaging && this.paging) {
      this.paging.enabled = false;
      if (this.scrolling) {
        this.scrolling.mode = 'virtual';
      }
      let pageSize: number;
      if (dataSource) {
        if (Array.isArray(dataSource)) {
          pageSize = dataSource.length;
        } else if (dataSource.totalCount) {
          pageSize = dataSource?.totalCount();
        } else {
          pageSize = 50;
        }
      }
      if (pageSize > 0) {
        this.paging.pageSize = pageSize;
      } else {
        this.paging.pageSize = 0;
      }
    }
  }

  private updateSimpleMode() {
    if (this.simpleMode && this.instance?.element()) {
      for (const div of Array.from(this.instance.element().getElementsByTagName('div'))) {
        if (!div.className.includes(' abc-datagrid-simple')) {
          div.className += ' abc-datagrid-simple';
        }
      }
    }
    if (this.simpleMode && this.groupPanel) {
      this.groupPanel.visible = false;
      this.groupPanel.emptyPanelText = null;
      this.groupPanel.allowColumnDragging = false;
    }
  }

  private async ensureStorage(): Promise<IStorage> {
    let storage: IStorage;
    if (this.storageSettings.storage) {
      storage = await this.storageSettings.storage;
    }
    if (!storage) {
      storage = new DefaultStorage();
      this.storageSettings.storage = Promise.resolve(storage);
    }
    return storage;
  }

  private async setState(key: string, state: any): Promise<void> {
    if (key) {
      await (await this.ensureStorage()).setItem(key, state);
    }
  }

  private async getState(key: string): Promise<any> {
    if (key) {
      return await (await this.ensureStorage()).getItem(key);
    }
  }

  public syncSettings(): void {
    if (this.stateStoring) {
      this.stateStoring.storageKey = this.storageSettings.key;
      const wasEnabled = this.stateStoring.enabled;
      this.stateStoring.enabled = !!this.storageSettings.key;
      if (!wasEnabled && this.stateStoring.enabled) {
        this.enableStateStoring(true);
      }
    }
  }

  public scrollToTop(): number {
    return this.performScroll((_) => _.scrollTop());
  }

  public scrollTo(targetLocation: any): void {
    this.performScroll((_) => _.scrollTo(targetLocation));
  }

  private performScroll<T>(action: (scrollable: Scrollable) => T): T {
    if (action && this.instance?.getScrollable) {
      const scrollable = this.instance.getScrollable();
      if (scrollable) {
        return action(scrollable);
      }
    }
  }

  public async exportToExcel() {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Table-1');
    const fileReader = new FileReader();
    const excelSettings: ExcelExportDataGridProps = {
      component: this.instance,
      autoFilterEnabled: true,
      worksheet: worksheet,
    };
    await exportDataGridToExcel(excelSettings);
    const buffer = await workbook.xlsx.writeBuffer();
    const fileBlob = new Blob([buffer], { type: 'application/octet-stream' });
    const fileName: string = 'DataGrid.xlsx';
    saveAs(fileBlob, fileName);
  }

  public async exportToPDF() {
    const pdf = new jsPDF();

    await exportDataGridToPDF({
      jsPDFDocument: pdf,
      component: this.instance,
      indent: 5,
    });

    pdf.save('DataGrid.pdf');
  }

  public deleteColumn(columnName: string): void {
    const gridColumnIndex = this.columns?.findIndex((_) => _.name === columnName);
    if (gridColumnIndex >= 0) {
      CollectionHelper.removeAt(this.columns, gridColumnIndex);
    }
    this.instance?.columnOption(columnName, 'filterValue', null);
    this.instance?.deleteColumn(columnName);
  }

  public async loadCustomColumnsFromLayout(): Promise<CustomQueryColumn[]> {
    const customColumns: CustomQueryColumn[] = [];
    await this.getAndEnsureStoredLayoutState(customColumns);
    if (this.loadCustomColumn) {
      for (const c of customColumns) {
        this.loadCustomColumn(c);
      }
    }
    return customColumns;
  }

  public isTotalSumAllowed(column: string | { dataType?: DataType }): boolean {
    let c: { dataType?: DataType };
    if (typeof column === 'string') {
      c = this.columns?.find((_) => _.name === column);
    } else {
      c = column;
    }
    return c?.dataType === 'number';
  }

  public getTotalSumVisibility(columnName: string): boolean {
    return !!this.prepareSummaryTotalItem(SummaryItemType.sum, columnName, false, false);
  }

  public setTotalSumVisibility(columnName: string, visible: boolean): void {
    this.prepareSummaryTotalItem(SummaryItemType.sum, columnName, visible, !visible);
    this.instance.refresh();
  }

  private ensureSummaryTotalItem(summaryType: SummaryItemType, columnName: string): void {
    this.prepareSummaryTotalItem(summaryType, columnName, true, false);
  }

  private removeSummaryTotalItem(summaryType: SummaryItemType, columnName: string): void {
    this.prepareSummaryTotalItem(summaryType, columnName, false, true);
  }

  private prepareSummaryTotalItem(
    summaryType: SummaryItemType,
    columnName: string,
    createIfNotExists: boolean,
    removeIfExists: boolean
  ): any {
    let s = this.summary;
    if (!s) {
      s = {};
      this.summary = s;
    }
    if (!s.totalItems) {
      s.totalItems = [];
    }
    if (!s.groupItems) {
      s.groupItems = [];
    }
    let resultItem: any = undefined;
    for (const items of [s.totalItems, s.groupItems]) {
      if (summaryType === SummaryItemType.count) {
        CollectionHelper.removeByFilter(items, (_) => _.summaryType === summaryType && _.column !== columnName);
      }
      if (columnName) {
        let item = items.find((_) => _.summaryType === summaryType && _.column === columnName);
        if (!item && createIfNotExists) {
          item = {
            summaryType: summaryType,
            column: columnName,
          };
          if (summaryType === SummaryItemType.count) {
            item.customizeText = (e) => {
              let text: string;
              if (CompareHelper.isNullorEmpty(e.value)) {
                text = '';
              } else {
                text = formatMessage('dxDataGrid-summaryCount', e.value ? e.value : '-');
              }
              return text;
            };
          } else if (summaryType === SummaryItemType.sum) {
            const c = this.columns?.find((_) => _.name === columnName);
            if (c) {
              item.valueFormat = c.format;
            }
            item.displayFormat = '∑={0}';
            if (items === s.groupItems) {
              item.alignByColumn = true;
            }
          }
          items.push(item);
        } else if (item && removeIfExists) {
          CollectionHelper.removeItem(items, item);
        }
        if (items === s.totalItems) {
          resultItem = item;
        }
      } else if (removeIfExists) {
        CollectionHelper.removeByFilter(items, (_) => _.summaryType === summaryType);
      }
    }
    return resultItem;
  }
}
